import * as React from 'react';
import {FC, useCallback, useContext, useEffect, useMemo} from 'react';
import {
    ClassificationDTO,
    MaintenanceStatus, OptionTDO, ServiceChangeKey,
    SortingItem,
    SupportTicketDTO, SupportTicketStatusDTO,
    SupportTicketStatusLabelDTO,
    TicketPriority
} from "../../Models/ApiModels";
import {AppStoreContext} from "../../Stores/AppStore";
import {observer} from "mobx-react";
import {Button, SelectBox,} from "devextreme-react";
import styles from "./Service.module.scss";
import {NoDataHint} from "./components/Hints";
import {useHistory} from 'react-router-dom';
import {AppRouting} from "../../AppConstants";
import {GetOptionsFromList, ListFilterSorting} from "./components/ListFilterSorting";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCircleExclamation} from '@fortawesome/free-solid-svg-icons';
import {authService} from "../../index";

export const renderWarningIcon = (data?: SupportTicketDTO) => {
    const isMarkForClosingOrClosed = data && (data.status === SupportTicketStatusDTO.MarkedForClosing || data.status === SupportTicketStatusDTO.UnsolvedClosed);
    const isWarning = data && data.isInCompletedTask && isMarkForClosingOrClosed;
    return ( isWarning && <div title={'The ticket has a incomplete maintenance task associated'} style={{ display: 'inline'}}><FontAwesomeIcon icon={faCircleExclamation} style={{ color: 'red', fontSize: '16px', marginLeft: '10px' }} />
        </div>
    )
}
const Tickets = observer(() => {
    const context = useContext(AppStoreContext);
    const history = useHistory();
    const {
        loadTickets,
        tickets,
        ticketsList: ticketsItems,
        filtersStore,
        checklist
    } = context;
    const { isGlobalSupport, isGlobalAdmin } = authService;

    useEffect(() => {
        void loadTickets();
    }, [filtersStore.selected, loadTickets]);

    const isGlobalRole = isGlobalAdmin || isGlobalSupport;

    const ticketsList = React.useMemo(() => {
        return ticketsItems?.map?.((ticket) => {
            const checklistItem = checklist?.find?.(it => it.ticketId === ticket.id);
            return {
                ...ticket,
                isInCompletedTask: Boolean(checklistItem && checklistItem.status === MaintenanceStatus.Pending),
            }
        })
    }, [checklist, ticketsItems]);

    const ticketSortingItems: SortingItem[] = useMemo(() => [
        {key: 'title', label: 'Title'},
        {key: 'description', label: 'Description'},
        {key: 'modifiedOn', label: 'Modified On'},
        {key: 'createdOn', label: 'Created On'},
        {key: 'resolvedOn', label: 'Resolved On'},
        {key: 'priority', label: 'Priority'},
    ], []);

    const ticketFilterItems: SortingItem[] = useMemo(() => [
        {key: 'status', label: 'Status', options: GetOptionsFromList(tickets?.map(t => t?.status??''), SupportTicketStatusLabelDTO)},
        {key: 'deviceId', label: 'Device', options: GetOptionsFromList(tickets?.map(t => t.deviceId))},
        {key: 'classification', label: 'Classification', options: GetOptionsFromList(tickets?.map(t => t.classification), ClassificationDTO)},
        {key: 'priority', label: 'Priority', options: GetOptionsFromList(tickets?.map(t => t.priority))},
    ], [tickets]);


    const _newItem = useCallback(() => {
        history.push(`${AppRouting.Tickets}/new`);
    }, [history]);

    const gridFiltersItems: OptionTDO[] = [
        {
            value: ServiceChangeKey.isNotResolved,
            label: 'Not Resolved'
        },
        {
            value: ServiceChangeKey.isResolved,
            label: 'Resolved'
        },
    ];
    const customFilters = (
        <div className={styles.filters}>
            <SelectBox
                className={styles.select}
                defaultValue={context.serviceUpdatedFilter}
                items={gridFiltersItems}
                label={'Resolution'}
                onValueChange={context.onServiceUpdatedChange}
                valueExpr="value"
                displayExpr="label"
            />
        </div>
    );

    return (
        <>
            <div className={styles.title}>
                Ticket List
                <div className={styles.titleActions}>
                    <Button className={styles.action} disabled={!filtersStore.selected.plant || isGlobalRole} onClick={_newItem}>
                        Open New Ticket
                    </Button>
                    <div hidden={Boolean(filtersStore.selected.plant && isGlobalRole)} className={styles.headerActionHint}>
                        Select plant first <FontAwesomeIcon icon={faCircleExclamation}/>
                    </div>
                </div>
            </div>
            <ListFilterSorting
                sortingItems={ticketSortingItems}
                filterItems={ticketFilterItems}
                customFilters={customFilters}
            />

            <div className={styles.list}>
                {ticketsList !== null && !ticketsList?.length && <NoDataHint message="Empty ticket data"/>}
                {!!ticketsList?.length &&
                    <div className={styles.listContainer}>
                        {ticketsList.map(ticket => <TicketItem key={ticket.id} ticket={ticket}/>)}
                    </div>
                }
            </div>
        </>
    );
});

const TicketItem: FC<{ ticket: SupportTicketDTO }> = observer(({ticket}) => {
    const {description, title, userId} = ticket;
    const context = useContext(AppStoreContext);
    const user = context.ticketsUsers.find(e => e.id === userId);
    const history = useHistory();

    const _onClick = useCallback(() => {
        history.push(`${AppRouting.Tickets}/${ticket.id}`);
    }, [history, ticket.id]);
    const priorityColor = ticket.status !== SupportTicketStatusDTO.MarkedForClosing ? ticket.priority === TicketPriority.High ? styles.high : ticket.priority === TicketPriority.Normal ? styles.normal : styles.low : '';
    return (
        <div className={`${styles.listItem} ${styles.listPriority} ${priorityColor}`} onClick={_onClick}>
            <div className={styles.listDesc}>
                {title}
                {renderWarningIcon(ticket)}
            </div>
            <div className={styles.listComment}>
                {description}
            </div>
            <div className={styles.listUser}>{user?.displayName}</div>
        </div>
    );
});


export default Tickets;